import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import {
  Info as InfoIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";

// JSON Syntax Highlighter Component
export const JsonSyntaxHighlighter = ({ jsonString, compact = false }) => {
  const syntaxHighlight = (json) => {
    json = json
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return json.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
      function (match) {
        let cls = "number";
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = "key";
          } else {
            cls = "string";
          }
        } else if (/true|false/.test(match)) {
          cls = "boolean";
        } else if (/null/.test(match)) {
          cls = "null";
        }
        return '<span class="' + cls + '">' + match + "</span>";
      }
    );
  };

  const highlightedJson = syntaxHighlight(jsonString);

  return (
    <Box
      component="pre"
      sx={{
        fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
        fontSize: compact ? "0.7rem" : "0.75rem",
        lineHeight: compact ? 1.2 : 1.4,
        margin: 0,
        padding: compact ? "4px" : "8px",
        backgroundColor: compact ? "transparent" : "rgba(0, 0, 0, 0.02)",
        borderRadius: compact ? 0 : "4px",
        border: compact ? "none" : "1px solid rgba(0, 0, 0, 0.1)",
        whiteSpace: "pre-line",
        wordBreak: "break-word",
        overflow: "auto",
        textAlign: "left",
        "& .string": { color: "#22863a" },
        "& .number": { color: "#005cc5" },
        "& .boolean": { color: "#d73a49" },
        "& .null": { color: "#6f42c1" },
        "& .key": { color: "#032f62", fontWeight: "bold" },
      }}
      dangerouslySetInnerHTML={{ __html: highlightedJson }}
    />
  );
};

// JSON Formatter Component with truncation (no expand/collapse)
export const JsonFormatter = ({ data, maxWidth = 300, compact = false }) => {
  if (!data) return <span>-</span>;

  // Convert to string without any parsing - just use the raw data
  let rawString =
    typeof data === "string" ? data : JSON.stringify(data, null, 2);

  // Take only the first N characters from the very beginning
  const maxChars = compact ? 150 : 250;
  const isTruncated = rawString.length > maxChars;

  if (isTruncated) {
    // Always truncate from position 0 (the beginning)
    let truncated = rawString.substring(0, maxChars);

    // Try to end at a complete line for cleaner display
    const lastNewline = truncated.lastIndexOf("\n");
    if (lastNewline > maxChars * 0.6) {
      truncated = truncated.substring(0, lastNewline);
    }

    rawString = truncated.trimEnd() + "...";
  }

  return (
    <Box sx={{ maxWidth, wordBreak: "break-word", position: "relative" }}>
      <JsonSyntaxHighlighter jsonString={rawString} compact={compact} />
      {isTruncated && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            px: 0.5,
            fontSize: "0.7rem",
            color: "text.secondary",
            fontStyle: "italic",
          }}
        >
          ...
        </Box>
      )}
    </Box>
  );
};

// Enhanced JSON Modal Component with copy functionality
export const JsonModal = ({ data, children }) => {
  const [open, setOpen] = useState(false);

  if (!data) return children;

  // Enhanced JSON formatting for modal
  const getFormattedJson = (rawData) => {
    let jsonData;

    try {
      // If it's a string, try to parse it
      if (typeof rawData === "string") {
        // First try to parse as JSON
        try {
          jsonData = JSON.parse(rawData);
        } catch {
          // If parsing fails, it might be a Java toString() format with nested objects
          if (rawData.includes("=")) {
            // Parse Java toString() format that can include nested objects
            const parseJavaToString = (str) => {
              let content = str.trim();

              // Remove outer braces if present
              if (content.startsWith("{") && content.endsWith("}")) {
                content = content.slice(1, -1);
              }

              const result = {};
              let i = 0;

              while (i < content.length) {
                // Skip whitespace and commas
                while (
                  i < content.length &&
                  (content[i] === " " || content[i] === ",")
                ) {
                  i++;
                }

                if (i >= content.length) break;

                // Find the key (everything before '=')
                let keyStart = i;
                while (i < content.length && content[i] !== "=") {
                  i++;
                }

                if (i >= content.length) break;

                const key = content.slice(keyStart, i).trim();
                i++; // skip '='

                // Find the value
                let value;
                let valueStart = i;

                // Skip whitespace
                while (i < content.length && content[i] === " ") {
                  i++;
                }

                if (i < content.length && content[i] === "{") {
                  // Nested object - find matching closing brace
                  let braceCount = 1;
                  let objStart = i;
                  i++; // skip opening brace

                  while (i < content.length && braceCount > 0) {
                    if (content[i] === "{") braceCount++;
                    else if (content[i] === "}") braceCount--;
                    i++;
                  }

                  const nestedStr = content.slice(objStart, i);

                  // Try to parse as JSON first (for cases like {"amount":"500"})
                  try {
                    value = JSON.parse(nestedStr);
                  } catch {
                    // If JSON parsing fails, try recursive parsing for Java toString format
                    value = parseJavaToString(nestedStr);
                  }
                } else {
                  // Simple value - find next comma or end
                  valueStart = i;
                  while (
                    i < content.length &&
                    content[i] !== "," &&
                    content[i] !== "}"
                  ) {
                    i++;
                  }

                  const rawValue = content.slice(valueStart, i).trim();

                  // Convert value to appropriate type
                  if (rawValue === "true" || rawValue === "false") {
                    value = rawValue === "true";
                  } else if (rawValue === "null") {
                    value = null;
                  } else if (rawValue === "") {
                    value = "";
                  } else if (!isNaN(rawValue) && !isNaN(parseFloat(rawValue))) {
                    value = parseFloat(rawValue);
                  } else {
                    // Check if the value is a JSON string
                    if (rawValue.startsWith('"') && rawValue.endsWith('"')) {
                      // Remove quotes and try to parse as JSON
                      const unquotedValue = rawValue.slice(1, -1);
                      try {
                        value = JSON.parse(unquotedValue);
                      } catch {
                        value = unquotedValue; // Keep as string if JSON parsing fails
                      }
                    } else if (
                      rawValue.startsWith("{") &&
                      rawValue.endsWith("}")
                    ) {
                      // Direct JSON object - try to parse it
                      try {
                        value = JSON.parse(rawValue);
                      } catch {
                        value = rawValue; // Keep as string if JSON parsing fails
                      }
                    } else {
                      value = rawValue;
                    }
                  }
                }

                result[key] = value;
              }

              return result;
            };

            try {
              jsonData = parseJavaToString(rawData);
            } catch (e) {
              // If all else fails, return the original string
              return rawData;
            }
          } else {
            return rawData;
          }
        }
      } else {
        jsonData = rawData;
      }

      // Format with proper indentation
      return JSON.stringify(jsonData, null, 2);
    } catch {
      // If everything fails, return the original data as string
      return typeof rawData === "string"
        ? rawData
        : JSON.stringify(rawData, null, 2);
    }
  };

  const formattedJsonString = getFormattedJson(data);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formattedJsonString);
      // You could add a toast notification here
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <>
      <Box onClick={() => setOpen(true)} sx={{ cursor: "pointer" }}>
        {children}
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "80vh",
            backgroundColor: "#f5f5f5",
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <InfoIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              JSON Data
            </Typography>
          </Box>
          <Box>
            <IconButton
              onClick={handleCopy}
              sx={{ color: "white" }}
              title="Copy to clipboard"
            >
              <CopyIcon />
            </IconButton>
            <IconButton
              onClick={() => setOpen(false)}
              sx={{ color: "white" }}
              title="Close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0, paddingTop: 0 }}>
          <Box
            sx={{
              maxHeight: "60vh",
              overflow: "auto",
              p: 2,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              m: 2,
              borderRadius: 1,
            }}
          >
            <JsonSyntaxHighlighter
              jsonString={formattedJsonString}
              compact={false}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
